// Presentation of a flag's creation date, shared by the list row and the detail header so the two
// can never word the same date differently.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MS_PER_DAY = 86400000;

/**
 * Parsed as local midnight rather than through `new Date(iso)`, which reads a bare `YYYY-MM-DD` as
 * UTC and so lands on the previous day for anyone behind UTC — a flag created on the 1st would read
 * as the 31st. Returns null for a missing or unparseable value so a bad date renders as blank
 * instead of `NaN` or `Invalid Date`.
 */
function parseIsoDate(iso: string | null | undefined): Date | null {
	if (!iso) {
		return null;
	}
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
	if (!match) {
		return null;
	}
	const [, year, month, day] = match;
	const date = new Date(Number(year), Number(month) - 1, Number(day));
	return Number.isNaN(date.getTime()) ? null : date;
}

/** `14 Apr 2026`. Empty string when there is no usable date. */
export function formatFlagDate(iso: string | null | undefined): string {
	const date = parseIsoDate(iso);
	if (!date) {
		return '';
	}
	return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * The flag's created day-of-month in `year`, clamped to that month's last day. Only 29 February is
 * affected: constructing it in a non-leap year would otherwise roll forward to 1 March, so an
 * anniversary would land a day late and report `364 days` instead of a clean year.
 */
function anniversaryIn(created: Date, year: number): Date {
	const lastDayOfMonth = new Date(year, created.getMonth() + 1, 0).getDate();
	return new Date(year, created.getMonth(), Math.min(created.getDate(), lastDayOfMonth));
}

/**
 * How long the flag has been carried, as `2 years 134 days old`.
 *
 * Counted in whole years plus the days since the most recent anniversary, rather than in total days,
 * because "912 days old" is hard to judge at a glance and the point of the column is to make a stale
 * flag obvious. The years part is dropped under a year, and the days part is kept at exactly zero
 * days so a flag added today reads `0 days old` rather than an empty label.
 *
 * `now` is injectable so tests are not tied to the clock; it defaults to the current time.
 */
export function formatFlagAge(iso: string | null | undefined, now: Date = new Date()): string {
	const created = parseIsoDate(iso);
	if (!created) {
		return '';
	}
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	if (created > today) {
		return '';
	}

	let years = today.getFullYear() - created.getFullYear();
	let anniversary = anniversaryIn(created, created.getFullYear() + years);
	if (anniversary > today) {
		years -= 1;
		anniversary = anniversaryIn(created, created.getFullYear() + years);
	}
	// Whole days: both sides are local midnight, so DST shifts cannot round this to a fraction.
	const days = Math.round((today.getTime() - anniversary.getTime()) / MS_PER_DAY);

	const parts: string[] = [];
	if (years > 0) {
		parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
	}
	if (days > 0 || years === 0) {
		parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
	}
	return `${parts.join(' ')} old`;
}
